import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { industriesDetail } from '@/mocks/industriesDetail';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface TechnologiesByIndustryProps {
  highlightIndustry?: string;
}

const techCategoryMap: Record<string, { category: string; icon: string; color: string }> = {
  'React': { category: 'Frontend', icon: 'ri-layout-masonry-line', color: 'bg-sky-500' },
  'Next.js': { category: 'Frontend', icon: 'ri-layout-masonry-line', color: 'bg-sky-500' },
  'React Native': { category: 'Mobile', icon: 'ri-smartphone-line', color: 'bg-violet-500' },
  'TypeScript': { category: 'Languages', icon: 'ri-code-s-slash-line', color: 'bg-blue-500' },
  'Node.js': { category: 'Backend', icon: 'ri-server-line', color: 'bg-green-500' },
  'Python': { category: 'Languages', icon: 'ri-code-s-slash-line', color: 'bg-blue-500' },
  'Java': { category: 'Languages', icon: 'ri-code-s-slash-line', color: 'bg-blue-500' },
  'Java / Spring': { category: 'Backend', icon: 'ri-server-line', color: 'bg-green-500' },
  'C++': { category: 'Languages', icon: 'ri-code-s-slash-line', color: 'bg-blue-500' },
  'PostgreSQL': { category: 'Database', icon: 'ri-database-2-line', color: 'bg-cyan-500' },
  'MongoDB': { category: 'Database', icon: 'ri-database-2-line', color: 'bg-cyan-500' },
  'Elasticsearch': { category: 'Database', icon: 'ri-database-2-line', color: 'bg-cyan-500' },
  'Kafka': { category: 'Data', icon: 'ri-bar-chart-grouped-line', color: 'bg-orange-500' },
  'Redis': { category: 'Database', icon: 'ri-database-2-line', color: 'bg-cyan-500' },
  'Snowflake': { category: 'Database', icon: 'ri-database-2-line', color: 'bg-cyan-500' },
  'AWS': { category: 'Cloud', icon: 'ri-cloud-line', color: 'bg-amber-500' },
  'AWS HIPAA': { category: 'Cloud', icon: 'ri-cloud-line', color: 'bg-amber-500' },
  'Azure IoT': { category: 'Cloud', icon: 'ri-cloud-line', color: 'bg-amber-500' },
  'Kubernetes': { category: 'DevOps', icon: 'ri-ship-line', color: 'bg-indigo-500' },
  'Terraform': { category: 'DevOps', icon: 'ri-ship-line', color: 'bg-indigo-500' },
  'Docker': { category: 'DevOps', icon: 'ri-ship-line', color: 'bg-indigo-500' },
  'TensorFlow': { category: 'AI/ML', icon: 'ri-brain-line', color: 'bg-rose-500' },
  'PyTorch': { category: 'AI/ML', icon: 'ri-brain-line', color: 'bg-rose-500' },
  'FHIR / HL7': { category: 'Health', icon: 'ri-hospital-line', color: 'bg-emerald-500' },
  'MQTT': { category: 'IoT', icon: 'ri-wifi-line', color: 'bg-lime-500' },
  'gRPC': { category: 'Backend', icon: 'ri-server-line', color: 'bg-green-500' },
  'Apache Flink': { category: 'Data', icon: 'ri-bar-chart-grouped-line', color: 'bg-orange-500' },
  'Google Maps API': { category: 'Integrations', icon: 'ri-map-pin-line', color: 'bg-teal-500' },
  'QGIS': { category: 'Integrations', icon: 'ri-map-pin-line', color: 'bg-teal-500' },
  'WebRTC': { category: 'Integrations', icon: 'ri-video-chat-line', color: 'bg-teal-500' },
  'Stripe': { category: 'Integrations', icon: 'ri-bank-card-line', color: 'bg-teal-500' },
  'Grafana': { category: 'Observability', icon: 'ri-eye-line', color: 'bg-fuchsia-500' },
  'Django': { category: 'Backend', icon: 'ri-server-line', color: 'bg-green-500' },
  'GraphQL': { category: 'Backend', icon: 'ri-server-line', color: 'bg-green-500' },
  'AWS Lambda': { category: 'Cloud', icon: 'ri-cloud-line', color: 'bg-amber-500' },
};

const categoryOrder = ['Languages', 'Frontend', 'Backend', 'Mobile', 'Database', 'Cloud', 'DevOps', 'AI/ML', 'Data', 'IoT', 'Integrations', 'Observability', 'Health'];

const categoryColors: Record<string, string> = {
  Languages: 'bg-blue-500',
  Frontend: 'bg-sky-500',
  Backend: 'bg-green-500',
  Mobile: 'bg-violet-500',
  Database: 'bg-cyan-500',
  Cloud: 'bg-amber-500',
  DevOps: 'bg-indigo-500',
  'AI/ML': 'bg-rose-500',
  Data: 'bg-orange-500',
  IoT: 'bg-lime-500',
  Integrations: 'bg-teal-500',
  Observability: 'bg-fuchsia-500',
  Health: 'bg-emerald-500',
};

function TechBadge({ tech, isHighlighted }: { tech: string; isHighlighted: boolean }) {
  const meta = techCategoryMap[tech] || { category: 'Other', icon: 'ri-code-s-slash-line', color: 'bg-gray-500' };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-300 ${
        isHighlighted
          ? 'bg-navy-900 text-white shadow-sm scale-105'
          : 'bg-gray-50 text-gray-600 border border-gray-100'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.color} flex-shrink-0`}></span>
      {tech}
    </span>
  );
}

function FeaturedIndustryCard({ industry, index }: { industry: (typeof industriesDetail)[0]; index: number }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  // Group tech by category
  const techByCategory: Record<string, string[]> = {};
  industry.keyTech.forEach((tech) => {
    const cat = techCategoryMap[tech]?.category || 'Other';
    if (!techByCategory[cat]) techByCategory[cat] = [];
    techByCategory[cat].push(tech);
  });
  const sortedCategories = categoryOrder.filter((c) => techByCategory[c] && techByCategory[c].length > 0);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, ...springTransition }}
      className="rounded-2xl border border-navy-900/20 bg-white shadow-xl shadow-navy-900/5 ring-1 ring-navy-900/10 overflow-hidden"
    >
      <div className="flex items-center gap-3 px-5 py-4 border-b border-navy-900/10 bg-navy-900/5">
        <div className={`w-10 h-10 flex items-center justify-center ${industry.accentColor} rounded-xl shadow-lg shadow-current/20`}>
          <i className={`${industry.icon} text-lg text-white`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-navy-900">{industry.name}</h3>
          <p className="text-xs text-gray-400 truncate">{industry.shortDesc}</p>
        </div>
        <span className="px-2.5 py-1 bg-navy-900 text-white text-[10px] font-bold rounded-lg uppercase tracking-wider whitespace-nowrap">
          Featured
        </span>
      </div>
      <div className="px-5 py-4 space-y-3">
        {sortedCategories.map((cat) => (
          <div key={cat} className="flex items-start gap-3">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20 flex-shrink-0 pt-1.5">
              {cat}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {techByCategory[cat].map((tech) => (
                <TechBadge key={tech} tech={tech} isHighlighted />
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function CompactIndustryCard({ industry, index, highlightId }: { industry: (typeof industriesDetail)[0]; index: number; highlightId?: string }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.04, ...springTransition }}
      className="rounded-xl border border-gray-100 bg-white p-4 hover:border-gray-200 hover:shadow-md transition-all duration-300"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 flex items-center justify-center ${industry.accentColor} rounded-lg shadow-lg shadow-current/20`}>
          <i className={`${industry.icon} text-sm text-white`}></i>
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-gray-800">{industry.name}</h3>
          <p className="text-[10px] text-gray-400 truncate">{industry.shortDesc}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-1">
        {industry.keyTech.slice(0, 6).map((tech) => (
          <TechBadge key={tech} tech={tech} isHighlighted={highlightId === industry.id} />
        ))}
        {industry.keyTech.length > 6 && (
          <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-medium text-gray-400 bg-gray-50 border border-gray-100">
            +{industry.keyTech.length - 6} more
          </span>
        )}
      </div>
    </motion.div>
  );
}

function FullIndustryRow({ industry, index }: { industry: (typeof industriesDetail)[0]; index: number }) {
  const [rowRef, rowInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const techByCategory: Record<string, string[]> = {};
  industry.keyTech.forEach((tech) => {
    const cat = techCategoryMap[tech]?.category || 'Other';
    if (!techByCategory[cat]) techByCategory[cat] = [];
    techByCategory[cat].push(tech);
  });
  const sortedCats = categoryOrder.filter((c) => techByCategory[c] && techByCategory[c].length > 0);

  return (
    <motion.div
      ref={rowRef}
      initial={{ opacity: 0, y: 20 }}
      animate={rowInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.06, ...springTransition }}
      className="rounded-2xl border border-gray-100 bg-white overflow-hidden hover:border-gray-200 hover:shadow-lg transition-all duration-300"
    >
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-50">
        <div className={`w-10 h-10 flex items-center justify-center ${industry.accentColor} rounded-xl shadow-lg shadow-current/20`}>
          <i className={`${industry.icon} text-lg text-white`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-gray-700">{industry.name}</h3>
          <p className="text-xs text-gray-400 truncate">{industry.shortDesc}</p>
        </div>
        <div className="text-xs font-bold text-gray-400 whitespace-nowrap">{industry.keyTech.length} tech</div>
      </div>
      <div className="px-5 py-4 space-y-3">
        {sortedCats.map((cat) => (
          <div key={cat} className="flex items-start gap-3">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider w-20 flex-shrink-0 pt-1.5">
              {cat}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {techByCategory[cat].map((tech) => (
                <TechBadge key={tech} tech={tech} isHighlighted={false} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function TechnologiesByIndustry({ highlightIndustry }: TechnologiesByIndustryProps) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const isCompact = !!highlightIndustry;

  const featured = industriesDetail.find((i) => i.id === highlightIndustry);
  const others = industriesDetail.filter((i) => i.id !== highlightIndustry);

  // Collect all unique categories used
  const allCategories = new Set<string>();
  industriesDetail.forEach((ind) => {
    ind.keyTech.forEach((tech) => {
      allCategories.add(techCategoryMap[tech]?.category || 'Other');
    });
  });
  const categoryLegend = categoryOrder.filter((c) => allCategories.has(c));

  return (
    <section className="py-12 md:py-20 bg-gray-50 relative overflow-hidden" ref={ref}>
      {/* Decorative orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-primary-100/40 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-amber-50/40 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-8 md:mb-12"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-stack-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">Tech Stack Intelligence</span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Technologies by
            <span className="text-primary-600"> Industry</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto px-2"
          >
            Every sector demands a different technology DNA. Here is how we map the right tech stack to each industry we serve.
          </motion.p>
        </motion.div>

        {/* Category legend */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, ...springTransition }}
          className="flex flex-wrap justify-center gap-2 mb-8 md:mb-10"
        >
          {categoryLegend.map((cat) => (
            <span
              key={cat}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-100 rounded-lg text-xs font-medium text-gray-600"
            >
              <span className={`w-2 h-2 rounded-full ${categoryColors[cat] || 'bg-gray-500'}`}></span>
              {cat}
            </span>
          ))}
        </motion.div>

        {/* Content: Featured + Compact Grid */}
        {isCompact && featured ? (
          <div className="space-y-6">
            <FeaturedIndustryCard industry={featured} index={0} />

            {/* Other industries in compact grid */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                Other Industries We Serve
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {others.map((industry, index) => (
                  <CompactIndustryCard
                    key={industry.id}
                    industry={industry}
                    index={index}
                    highlightId={highlightIndustry}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Full expanded view for non-highlighted usage (Industries overview page) */
          <div className="space-y-4">
            {industriesDetail.map((industry, index) => (
              <FullIndustryRow key={industry.id} industry={industry} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}